import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, Input, OnDestroy, forwardRef, inject
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    ControlValueAccessor, NG_VALUE_ACCESSOR
} from "@angular/forms";
import { MimeType } from "@shopliftr/common-js/data";
import { MatButton } from "@angular/material/button";

import { FileSelectorComponent } from "../../../components/file-selector/file-selector.component";
import { ISelectedFile } from "../../../components/file-selector/interface/selected-file";
import { FileService } from "../../../services/file/file.service";
import { IImageSize } from "../../../services/file/interface/image-size";
import { ImageCropperDialogService } from "../../../services/image-cropper/image-cropper-dialog.service";
import { ButtonDirective } from "../../../application/directives/button/button.directive";
import { EnhancedControlDirective } from "../../directives/form-control/enhanced-control.directive";
import { ComponentException } from "../../../exceptions/component-exception";
import { COMMON_APP_SERVICE } from "../../../application/constants/app-tokens";
import { IAppService } from "../../../application/interfaces/app-service";
import { IImageControlValue } from "./interface/image-control-value";

/**
 * A form control that holds an image.
 *
 * A wrapper form control: the host is focusable and hands focus to the first enabled button, is a tab stop only
 * while focus is outside it, and is marked touched only when focus leaves the whole control (see the Wrapper Form
 * Controls rules in `docs/angular-standards/components/06-form-controls.md`).
 *
 * Bind it with `formControlName` or `[formControl]`. Its value carries the image
 * to display and any pending upload, so a consuming form does not re-implement
 * the select-crop-preview flow.
 *
 * The cropper opens automatically only when both `width` and `height` are set
 * and the newly selected image does not already match them — otherwise the
 * image is accepted as-is. Cancelling an auto-opened crop abandons the whole
 * selection; cancelling a manually opened crop (the visible Crop button)
 * changes nothing.
 *
 * @example
 * ```html
 * <mgd-image formControlName="image" uploadButtonLabel="Upload New Image" />
 * ```
 */
@Component({
    selector: "mgd-image",
    templateUrl: "./image-control.component.html",
    styleUrl: "./image-control.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FileSelectorComponent, ButtonDirective, MatButton],
    hostDirectives: [EnhancedControlDirective],
    // Wrapper form control (docs/angular-standards/components/06-form-controls.md#wrapper-form-controls): the host is
    // focusable and hands focus to the first enabled button; focusout (not blur) bubbles from the descendants, and
    // touched is reported only when focus leaves the whole control.
    host: {
        "[attr.tabindex]": "tabIndex",
        "(focus)": "hostFocused()",
        "(focusin)": "hostFocusIn()",
        "(focusout)": "hostFocusOut($event)",
        "(pointerdown)": "hostPointerDown()",
        "(pointerup)": "hostPointerUp()",
        "(pointercancel)": "hostPointerCancel()",
        "(pointerleave)": "hostPointerCancel()"
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ImageControlComponent),
            multi: true
        }
    ]
})
export class ImageControlComponent implements ControlValueAccessor, OnDestroy {

    /**
     * The host is a tab stop (`0`) while the control is enabled and focus is outside it, and `-1` (focusable by
     * script, out of the tab order) while it is disabled or focus is inside. A permanent `0` would add a second tab
     * stop in front of the buttons and make Shift+Tab bounce back to the host.
     */
    protected get tabIndex(): number {

        return !this.isDisabled && !this._focusWithin ? 0 : -1;
    }

    /** Required output width. Setting both this and `height` makes the size a constraint. */
    @Input() width?: number;

    /** Required output height. */
    @Input() height?: number;

    /** Image shown de-emphasised when the control is empty. */
    @Input() fallbackUrl?: string;

    /** Caption shown with the fallback image. */
    @Input() fallbackNote?: string;

    /** Corner badge over the frame. */
    @Input() badgeLabel?: string;

    /** Label for the upload button. */
    @Input() uploadButtonLabel = "Upload Image";

    /** Label for the crop button. */
    @Input() cropButtonLabel = "Crop";

    /** Label for the remove action inside the cropper dialog. Unset hides it. */
    @Input() clearButtonLabel?: string;

    /** Output mime type, forwarded to the cropper. */
    @Input() mimeType: MimeType = MimeType.Png;

    /**
     * Whether the embedded file selector listens for a pasted image.
     *
     * `FileSelectorComponent`'s paste listener is document-scoped, not
     * element-scoped, so a page with more than one `mgd-image` on it (e.g. one
     * per locale) would have a single paste committed into every instance at
     * once. Set to `false` on every instance but the one that should receive
     * paste on such a page.
     */
    @Input() listenForPaste = true;

    /**
     * Hint shown alongside the actions when paste is enabled. Empty string hides it.
     *
     * Defaults to a non-empty string deliberately: `listenForPaste` is already `true` by
     * default, so without this every existing consumer would have a paste capability with no
     * way for a user to discover it short of trying it on a hunch or pasting the wrong thing
     * and reading the rejection. A consumer that words the capability elsewhere in its own UI
     * can pass an empty string to opt out.
     */
    @Input() pasteHintLabel = "or paste an image from the clipboard";

    /**
     * Identifies which logical control this instance is currently showing, for a consumer
     * that rebinds `[formControl]` to a different control on the fly (e.g. one `mgd-image`
     * re-pointed across per-language controls as a Localized Domain Form's tab changes).
     *
     * Object-URL ownership is tracked per key rather than as one slot on the component: a
     * rebind is a view change, not a value replacement, so switching away from a key must
     * never revoke the blob URL still legitimately stored on that key's own control — see
     * `_commit` and `ngOnDestroy`. Consumers with a single, never-rebound control can leave
     * this unset; every operation then keys off `undefined` uniformly, equivalent to the old
     * single-slot behavior.
     */
    @Input() controlKey?: unknown;

    /**
     * Accepted upload types. Defaults to JPEG/PNG only — deliberately narrower than
     * `Files.ACCEPTED_IMAGE_TYPES`, which also admits GIF and SVG. SVG is rejected by default
     * for two reasons beyond preference: it can carry inline `<script>` and is served back to
     * users (an XSS vector), and it doesn't survive this control anyway — `onCropClick` routes
     * through a canvas-based cropper, which rasterizes a vector image on crop into a worse
     * result than what was pasted, with no indication why. Override only when a specific
     * consumer legitimately needs a broader set (e.g. GIF).
     */
    @Input() acceptedTypes: ReadonlyArray<MimeType> = [MimeType.Jpeg, MimeType.PJpeg, MimeType.Png];

    value?: IImageControlValue;

    isDisabled = false;

    private _focusWithin = false;

    private _pointerInside = false;

    private readonly _fileService = inject(FileService);

    private readonly _imageCropperDialog = inject(ImageCropperDialogService);

    private readonly _app = inject<IAppService>(COMMON_APP_SERVICE);

    private readonly _cdRef = inject(ChangeDetectorRef);

    private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Object URLs this control owns, keyed by `controlKey`, so each key's pending blob is
     * revoked independently of whichever key is currently displayed.
     */
    private readonly _ownedUrls = new Map<unknown, string>();

    /**
     * Revokes every object URL still owned, across every key, when the control is destroyed.
     *
     * Abandoning an in-progress edit — navigating away, or the form being torn down — is an
     * ordinary user action, not just a value replacement. Without this, an object URL the
     * control created would never be revoked and would leak for the life of the tab. This
     * also catches a key the user pasted into but never revisited before the form closed.
     */
    ngOnDestroy(): void {

        for (const url of this._ownedUrls.values()) {
            URL.revokeObjectURL(url);
        }
    }

    /**
     * Displays whatever the form says this control's value is right now.
     *
     * Deliberately does not revoke anything: `writeValue` fires identically whether the
     * same control's value changed externally or `[formControl]` just rebound to a
     * different control entirely (a rebind carries no signal distinguishing the two), so
     * revoking here risks destroying a blob URL another key's control still legitimately
     * owns. Revocation happens only where the key is known for certain: `_commit` (this
     * component's own replacement, always for the current key) and `ngOnDestroy` (final
     * cleanup, every key).
     */
    writeValue(value?: IImageControlValue): void {

        this.value = value;
        this._cdRef.markForCheck();
    }

    registerOnChange(fn: (value?: IImageControlValue) => void): void {

        this._emitChanged = fn;
    }

    registerOnTouched(fn: () => void): void {

        this._emitTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {

        this.isDisabled = isDisabled;
        if (isDisabled) {
            // The focused button is disabled without a focusout; do not leave the host pinned at tabindex -1
            this._focusWithin = false;
        }
        this._cdRef.markForCheck();
    }

    /**
     * Handles a file chosen through the picker or pasted.
     *
     * The cropper opens automatically only when a required size is set and the
     * image does not match it — otherwise the image is accepted as-is.
     *
     * @param selected - The chosen file and its object URL
     */
    onFileSelected(selected: ISelectedFile): void {

        this._fileService.getImageSize$(selected.file)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (size: IImageSize) => {

                    const requiresSize = !!this.width && !!this.height;
                    const matches = size.width === this.width && size.height === this.height;

                    if (requiresSize && !matches) {
                        this._openCropper(selected.url, true);
                        return;
                    }

                    this._commit({
                        imageUrl: selected.url,
                        blob: selected.file,
                        size: size
                    }, selected.url);
                },
                error: () => {

                    // FileSelectorComponent only filters by extension, so a
                    // corrupt or mislabeled file can still reach here and
                    // fail to decode. Without this, the selection's object
                    // URL would leak and the failure would surface only as
                    // an unhandled RxJS error.
                    URL.revokeObjectURL(selected.url);

                    const message = `Could not read image "${selected.file.name}": the file may be corrupt or not actually an image.`;

                    this._app.errorHandler.handleComponentError(new ComponentException(message));
                    this._app.notificationService.displayErrorMessage(message);
                }
            });
    }

    /**
     * Opens the cropper on the current image. Only available while `value.blob` is set — a
     * pending, not-yet-uploaded selection whose `imageUrl` is a same-origin object URL.
     *
     * A persisted image's `imageUrl` points at this app's remote asset CDN, which does not
     * currently send CORS headers; loading it into the cropper's `crossorigin="anonymous"`
     * `<img>` (required so cropperjs's canvas export isn't tainted) fails outright. Until that
     * CDN is CORS-enabled, re-cropping an already-persisted image isn't possible, so the
     * button (gated identically in the template) isn't shown for one.
     */
    onCropClick(): void {

        if (this.value?.blob) {
            this._openCropper(this.value.imageUrl, false);
        }
    }

    /** Moves focus to the first enabled button; a disabled control has nothing to focus. */
    focus(): void {

        if (!this.isDisabled) {
            this._elementRef.nativeElement.querySelector<HTMLButtonElement>("button:not([disabled])")?.focus();
        }
    }

    /** Focusing the host hands focus to the first enabled button (the upload button). */
    protected hostFocused(): void {

        this.focus();
    }

    /** Records that focus is on the host or inside it, which takes the host out of the tab order. */
    protected hostFocusIn(): void {

        this._focusWithin = true;
    }

    /**
     * Marks the control touched only when focus leaves the whole control. Moving between its own buttons is not a
     * blur, and neither is a `null` related target caused by pressing a button in a browser that does not focus it
     * on click (Safari, Firefox on macOS).
     */
    protected hostFocusOut(event: FocusEvent): void {

        const next = event.relatedTarget as Node | null;
        if (next && this._elementRef.nativeElement.contains(next)) {
            return;
        }

        if (!next && this._pointerInside) {
            return;
        }

        this._focusWithin = false;
        this._emitTouched();
    }

    /** A press inside the control: a `null` related target on the next `focusout` is not the control being left. */
    protected hostPointerDown(): void {

        this._pointerInside = true;
    }

    /** Gives focus back to the first button when the press left it nowhere, so the control can later be left (and touched). */
    protected hostPointerUp(): void {

        this._pointerInside = false;
        if (!this.isDisabled && !this._elementRef.nativeElement.contains(document.activeElement)) {
            this.focus();
        }
    }

    /** The press ended without a release on the control (cancelled, or the pointer left while pressed). */
    protected hostPointerCancel(): void {

        this._pointerInside = false;
    }

    // Arrow-function-valued fields rank as methods for member-ordering, so
    // these CVA callback slots are declared here, alongside the private
    // methods, rather than up with the other private fields.
    private _emitChanged: (value?: IImageControlValue) => void = () => {};

    private _emitTouched: () => void = () => {};

    /**
     * Opens the cropper dialog and applies whatever it returns.
     *
     * @param imageSource - The image to crop
     * @param abandonOnCancel - True when the crop is the step that makes a
     * newly selected image usable, so cancelling discards it
     */
    private _openCropper(imageSource: string, abandonOnCancel: boolean): void {

        this._imageCropperDialog.openImageCropper$({
            imageSource: imageSource,
            width: this.width,
            height: this.height,
            mimeType: this.mimeType,
            removeLabel: this.clearButtonLabel
        })
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (result) => {

                    if (abandonOnCancel) {
                        // On the auto-open path, imageSource is the pre-crop
                        // object URL FileSelectorComponent created for the
                        // newly selected file. The committed value never
                        // reuses that specific URL on any branch below — a
                        // confirmed crop produces a fresh URL from the
                        // cropped blob, and remove/cancel have no URL at all
                        // — so it is revoked here unconditionally, or it
                        // leaks for the life of the tab.
                        URL.revokeObjectURL(imageSource);
                    }

                    if (!result || (result.action === "crop" && !result.image)) {
                        // Cancel never changes the data. Neither does a "crop"
                        // result with no image — the dialog's own Crop button
                        // is a no-op until something has been latched, but this
                        // guards defensively regardless of how the dialog got
                        // implemented.
                        return;
                    }

                    if (result.action === "remove") {
                        this._commit(undefined);
                        // The Crop button that opened the dialog no longer exists, so the dialog cannot hand focus
                        // back to it: keep focus inside the control, on the upload button
                        this.focus();
                        return;
                    }

                    const url = URL.createObjectURL(result.image.image);
                    this._commit(
                        {
                            imageUrl: url,
                            blob: result.image.image,
                            size: {
                                width: result.image.width,
                                height: result.image.height
                            }
                        },
                        url
                    );
                }
            });
    }

    /**
     * Sets the value and notifies the form, replacing whatever this control previously
     * owned for the current key.
     *
     * @param value - The new value, or undefined to clear
     * @param ownedUrl - An object URL this control created and must revoke later
     */
    private _commit(value?: IImageControlValue, ownedUrl?: string): void {

        const previouslyOwned = this._ownedUrls.get(this.controlKey);
        if (previouslyOwned && previouslyOwned !== ownedUrl) {
            URL.revokeObjectURL(previouslyOwned);
        }
        if (ownedUrl) {
            this._ownedUrls.set(this.controlKey, ownedUrl);
        }
        else {
            this._ownedUrls.delete(this.controlKey);
        }
        this.value = value;
        this._emitChanged(value);
        this._cdRef.markForCheck();
    }
}
